<script lang="ts">
  // Workout · Complete: a bold "workout complete" card with the difficulty and four big numbers.
  import type { ReportContext } from '../context'
  import { statsFor } from '../stats'
  import Badges from '../parts/Badges.svelte'
  import Brand from '../parts/Brand.svelte'
  import Diamonds from '../parts/Diamonds.svelte'
  import Fill from '../parts/Fill.svelte'
  import Frame from '../parts/Frame.svelte'
  import Icon from '../parts/Icon.svelte'
  import PowerChart from '../parts/PowerChart.svelte'
  import TitleBlock from '../parts/TitleBlock.svelte'

  export let ctx: ReportContext

  $: w = ctx.data.workout!
  $: a = ctx.data.activity
  // Focus names vary ("Pursuiter", "GC Specialist", "Breakaway Specialist"); shrink long ones to fit the tile.
  $: focusFont = (name: string) => {
    const base = { portrait: 60, square: 40, landscape: 58 }[ctx.size]
    const fits = { portrait: 11, square: 9, landscape: 10 }[ctx.size]
    return Math.round(Math.max(base * 0.62, Math.min(base, (base * fits) / name.length)))
  }
  $: chartHeight = { portrait: 128, square: 96, landscape: 130 }[ctx.size]
  $: tiles = statsFor(ctx, ['stat.xss', 'stat.time', 'stat.xep', 'stat.focus', 'stat.power', 'stat.carbs']).slice(0, 4)
</script>

<Frame {ctx} base="linear-gradient(165deg, #1b2125 0%, #0a0c0d 100%)" shade="even">
  <svelte:fragment slot="decor">
    <div class="xr-band" />
    <svg class="xr-mark" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 1 23 12 12 23 1 12Z" /></svg>
  </svelte:fragment>

  <div class="xr-layout">
    <div class="xr-top">
      <span class="xr-stamp"><Icon name="check" size={30} stroke={3.4} color="#0b1114" /> Workout Complete</span>
      <Brand height={40} />
    </div>

    <h2 class="xr-name" class:xr-long={w.name.length > 24}>{w.name}</h2>

    {#if ctx.show('difficulty') && a.difficulty_stars}
      <div class="xr-diff">
        <Diamonds value={a.difficulty_stars} size={ctx.size === 'square' ? 52 : 64} color="var(--accent)" />
        <span>{a.difficulty_label ?? ''}{a.difficulty ? ` · ${Math.round(a.difficulty)}` : ''}</span>
      </div>
    {/if}

    {#if tiles.length}
      <div class="xr-tiles">
        {#each tiles as t}
          <div class="xr-tile">
            <span class="xr-tile-label">{ctx.size === 'square' ? t.short : t.label}</span>
            <span class="xr-tile-value" style={t.key === 'stat.focus' && a.focus ? `font-size: ${focusFont(a.focus)}px;` : ''}>{t.key === 'stat.focus' && a.focus ? a.focus : t.value}{#if t.unit && t.unit !== 'XSS'}<small>{t.unit}</small>{/if}</span>
            {#if t.key === 'stat.focus'}<span class="xr-tile-sub">{t.value} focus</span>{/if}
          </div>
        {/each}
      </div>
    {/if}

    {#if ctx.show('chart') && ctx.data.series?.power}
      <div class="xr-chart">
        <Fill minHeight={chartHeight} let:width>
          <PowerChart series={ctx.data.series} colored={ctx.options.colorPowerChart} showHr={ctx.options.chartHeartRate} {width} height={chartHeight} showTarget={w.has_targets} showMpa={false} fillOpacity={0.6} baseline={false} />
        </Fill>
      </div>
    {/if}

    <div class="xr-footer">
      <TitleBlock {ctx} lines={1} />
      <Badges {ctx} own="workout" max={ctx.size === 'square' ? 2 : Infinity} />
    </div>
  </div>
</Frame>

<style>
  .xr-band {
    position: absolute;
    top: -260px;
    right: -320px;
    width: 900px;
    height: 640px;
    background: var(--accent);
    opacity: 0.16;
    transform: rotate(-24deg);
    border-radius: 60px;
  }
  .xr-mark {
    position: absolute;
    right: -120px;
    top: 330px;
    width: 560px;
    height: 560px;
    fill: var(--accent);
    opacity: 0.06;
  }
  .xr-layout {
    flex: 1;
    min-height: 0;
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: auto auto auto minmax(0, 1fr) auto auto auto;
    grid-template-areas: 'top' 'name' 'diff' '.' 'tiles' 'chart' 'footer';
    row-gap: 24px;
  }
  :global(.xr-portrait) .xr-name { margin-top: 20px; }
  :global(.xr-square) .xr-layout { row-gap: 22px; }
  /* Landscape: the workout on the left, its numbers beside it, the ride across the bottom. */
  :global(.xr-landscape) .xr-layout {
    grid-template-columns: minmax(0, 1fr) 680px;
    grid-template-rows: auto auto auto minmax(0, 1fr) auto auto;
    grid-template-areas:
      'top top'
      'name tiles'
      'diff tiles'
      '. tiles'
      'chart chart'
      'footer footer';
    column-gap: 70px;
    row-gap: 30px;
  }
  .xr-top { grid-area: top; }
  .xr-name { grid-area: name; }
  .xr-diff { grid-area: diff; }
  .xr-tiles { grid-area: tiles; }
  :global(.xr-landscape) .xr-tiles { align-self: center; }
  .xr-chart { grid-area: chart; }
  .xr-footer { grid-area: footer; }
  :global(.xr-square) .xr-name { font-size: 118px; }
  :global(.xr-square) .xr-name.xr-long { font-size: 92px; }
  :global(.xr-square) .xr-diff { font-size: 44px; }
  :global(.xr-portrait) .xr-tile-value { font-size: 76px; }
  /* Square: the four numbers in one row. */
  :global(.xr-square) .xr-tiles { grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 14px; }
  :global(.xr-square) .xr-tile { padding: 16px 18px; }
  :global(.xr-square) .xr-tile-label { font-size: 17px; }
  :global(.xr-square) .xr-tile-value { font-size: 52px; }
  :global(.xr-landscape) .xr-tile-value { font-size: 76px; }
  :global(.xr-landscape) .xr-name { font-size: 140px; }
  :global(.xr-landscape) .xr-name.xr-long { font-size: 110px; }
  .xr-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .xr-stamp {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 12px 26px 12px 18px;
    border-radius: 999px;
    background: var(--accent);
    color: #0b1114;
    font-size: 26px;
    font-weight: 800;
    letter-spacing: 0.14em;
    text-transform: uppercase;
  }
  .xr-name {
    font-family: var(--display);
    font-weight: 800;
    font-style: italic;
    font-size: 150px;
    line-height: 0.86;
    text-transform: uppercase;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
    overflow: hidden;
    overflow-wrap: anywhere;
  }
  .xr-diff {
    display: flex;
    align-items: center;
    gap: 22px;
    font-family: var(--display);
    font-size: 52px;
    font-weight: 700;
    text-transform: uppercase;
  }
  .xr-name.xr-long {
    font-size: 118px;
  }
  .xr-tiles {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 18px;
  }
  .xr-tile {
    display: flex;
    flex-direction: column;
    padding: 22px 26px;
    border-radius: 22px;
    background: var(--panel);
    border: 2px solid var(--faint);
    min-width: 0;
  }
  .xr-tile-label {
    font-size: 21px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--muted);
  }
  .xr-tile-value {
    margin-top: 6px;
    font-family: var(--display);
    font-weight: 800;
    font-size: 84px;
    line-height: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .xr-tile-value small {
    margin-left: 8px;
    font-size: 0.45em;
    color: var(--muted);
  }
  .xr-tile-sub {
    margin-top: 4px;
    font-size: 22px;
    color: var(--muted);
  }
  .xr-chart {
    display: flex;
  }
  .xr-footer {
    display: flex;
    flex-direction: column;
    gap: 22px;
  }
</style>
